import {
    AvailabilityStatus,
    CurrencyCode,
    Listing,
    ListingFreshness,
    ListingProductUrlSource,
    PriceUnknownComponent,
    PriceVerificationStatus,
    ProductCategoryPath,
    ProductCategoryRoot,
    ProductCategoryRootId,
    ProductSubcategory,
    SourceStatus
} from './domain';
import {
    PlatformAdapter,
    PlatformAdapterKind,
    PlatformListingDetail,
    PlatformPriceAndStock,
    PlatformRawListingBase,
    PlatformSearchProductsRequest
} from './platform-adapter';
import {
    PermissionGuardedPlatformAdapterRegistry,
    PlatformAdapterRegistration,
    PlatformPermissionRequirements
} from './platform-permission';
import { calculatePublicEffectivePrice, normalizeListingFreshness } from './price-utils';

export const naverShoppingProviderId = 'naver_shopping';
export const naverShoppingPlatformId = 'platform-naver-shopping';
export const naverShoppingPlatformName = '네이버 쇼핑 검색';
export const naverShoppingDefaultBaseUrl = 'https://openapi.naver.com/v1/search/shop.json';
export const NAVER_SHOPPING_OFFICIAL_DAILY_LIMIT = 25000;
export const NAVER_SHOPPING_DEFAULT_CACHE_TTL_MS = 60 * 1000;
export const NAVER_SHOPPING_DEFAULT_TIMEOUT_MS = 3000;
export const NAVER_SHOPPING_DEFAULT_RETRY_COUNT = 1;
export const NAVER_SHOPPING_DEFAULT_RETRY_DELAY_MS = 0;

export type NaverShoppingSort = 'sim' | 'date' | 'asc' | 'dsc';
export type NaverShoppingFilter = 'naverpay';
export type NaverShoppingExclude = 'used' | 'rental' | 'cbshop';
export type NaverShoppingProviderErrorKind =
    | 'invalid_request'
    | 'permission_denied'
    | 'rate_limited'
    | 'provider_unavailable'
    | 'network_timeout'
    | 'network_error';

export interface NaverShoppingSearchRequestInput {
    query?: string | null;
    category?: string | null;
    display?: number | string | null;
    start?: number | string | null;
    page?: number | string | null;
    sort?: string | null;
    filter?: string | null;
    exclude?: string | string[] | null;
}

export interface NaverShoppingHttpRequest {
    query: string;
    display: number;
    start: number;
    sort: NaverShoppingSort;
    filter?: NaverShoppingFilter;
    exclude?: string;
}

export interface NaverShoppingServerConfig {
    enabled: boolean;
    clientId: string | null;
    clientSecret: string | null;
    baseUrl: string;
    timeoutMs: number;
    retryCount: number;
    retryDelayMs: number;
    cacheTtlMs: number;
    dailyLimit: number;
}

export interface NaverShoppingHttpClientContext {
    baseUrl: string;
    clientId: string;
    clientSecret: string;
    timeoutMs: number;
}

export interface NaverShoppingHttpResponse {
    status: number;
    body: NaverShoppingApiResponse | NaverShoppingApiErrorBody | null;
}

export interface NaverShoppingHttpClient {
    search(request: NaverShoppingHttpRequest, context: NaverShoppingHttpClientContext): Promise<NaverShoppingHttpResponse>;
}

export interface NaverShoppingApiItem {
    title?: string;
    link?: string;
    image?: string;
    lprice?: string;
    hprice?: string;
    mallName?: string;
    productId?: string;
    productType?: string;
    maker?: string;
    brand?: string;
    category1?: string;
    category2?: string;
    category3?: string;
    category4?: string;
}

export interface NaverShoppingApiResponse {
    lastBuildDate?: string;
    total?: number;
    start?: number;
    display?: number;
    items?: NaverShoppingApiItem[];
}

export interface NaverShoppingApiErrorBody {
    errorMessage?: string;
    errorCode?: string;
    message?: string;
}

export type NaverShoppingProductTypeKind =
    | 'price_compare'
    | 'price_compare_unmatched'
    | 'used'
    | 'pre_release'
    | 'discontinued'
    | 'sale_stopped'
    | 'unknown';

export type NaverShoppingPriceCandidateStatus =
    | 'price_anchor'
    | 'needs_verification'
    | 'excluded_from_lowest';

export interface NaverShoppingProductTypeStatus {
    code: string | null;
    kind: NaverShoppingProductTypeKind;
    label: string;
    excludedFromLowest: boolean;
}

export interface NaverShoppingSearchHintProfile {
    brandName: string | null;
    makerName: string | null;
    categoryPathText: string;
    sameProductEvidence: false;
    confidence: 'low';
    needsVerification: true;
}

export interface NaverShoppingRawPayload {
    provider_id: typeof naverShoppingProviderId;
    api: 'naver_shopping_search';
    query_hash: string;
    cache: NaverShoppingCacheEvidence;
    request: Omit<NaverShoppingHttpRequest, 'query'>;
    item: NaverShoppingApiItem;
    price_note: string;
}

export interface NaverShoppingRawListing extends PlatformRawListingBase<NaverShoppingRawPayload> {
    naver_product_type: string | null;
    naver_product_type_status: NaverShoppingProductTypeStatus;
    mall_name: string | null;
    seller_display_name: string | null;
    platform_display_name: string;
    brand_name: string | null;
    maker_name: string | null;
    category_path_text: string;
    category_confidence: number;
    category_needs_verification: true;
    category_source: 'naver_category_hint';
    search_hints: NaverShoppingSearchHintProfile;
    price_candidate_status: NaverShoppingPriceCandidateStatus;
    unknown_price_components: PriceUnknownComponent[];
}

export interface NaverShoppingAdapterRuntimeState {
    enabled: boolean;
    credentialsConfigured: boolean;
    providerState: 'ready' | 'api_pending';
    sourceStatus: SourceStatus;
    reason: 'ready' | 'disabled' | 'missing_credentials';
}

export interface NaverShoppingCacheKeyParts {
    provider: typeof naverShoppingProviderId;
    version: 'v1';
    queryHash: string;
    category: string;
    sort: NaverShoppingSort;
    page: number;
    display: number;
}

export interface NaverShoppingCacheEvidence {
    cache_key: string;
    cache_key_parts: NaverShoppingCacheKeyParts;
    cache_ttl_ms: number;
    cached_at: string | null;
    cache_expires_at: string | null;
    cache_hit: boolean;
}

export interface NaverShoppingCacheEntry {
    key: string;
    parts: NaverShoppingCacheKeyParts;
    rawListings: NaverShoppingRawListing[];
    cachedAt: string;
    expiresAt: string;
    ttlMs: number;
}

export interface NaverShoppingSearchCache {
    get(key: string, now: Date): NaverShoppingCacheEntry | null;
    set(key: string, parts: NaverShoppingCacheKeyParts, rawListings: NaverShoppingRawListing[], now: Date, ttlMs: number): NaverShoppingCacheEntry;
}

export interface NaverShoppingRateLimitDecision {
    allowed: boolean;
    dayKey: string;
    consumed: number;
    remaining: number;
    limit: number;
}

export interface NaverShoppingRateLimitGuard {
    tryConsume(now: Date, limit: number): NaverShoppingRateLimitDecision;
    getSnapshot(now: Date, limit: number): NaverShoppingRateLimitDecision;
}

export interface NaverShoppingRetryStats {
    attempts: number;
    retries: number;
    lastErrorKind: NaverShoppingProviderErrorKind | null;
}

export interface NaverShoppingPlatformAdapterOptions {
    config?: Partial<NaverShoppingServerConfig>;
    env?: Record<string, string | undefined>;
    client?: NaverShoppingHttpClient;
    cache?: NaverShoppingSearchCache | null;
    rateLimitGuard?: NaverShoppingRateLimitGuard | null;
    now?: () => Date;
    sleep?: (ms: number) => Promise<void>;
}

export interface NaverShoppingPlatformAdapterRegistrationOptions extends NaverShoppingPlatformAdapterOptions {
    permissionEvidence?: PlatformAdapterRegistration<NaverShoppingRawListing>['permissionEvidence'];
    permissionNow?: Date | string;
}

const allowedSorts: NaverShoppingSort[] = ['sim', 'date', 'asc', 'dsc'];
const allowedFilters: NaverShoppingFilter[] = ['naverpay'];
const allowedExcludes: NaverShoppingExclude[] = ['used', 'rental', 'cbshop'];
const naverShoppingProductUrlSource: ListingProductUrlSource = 'naver_api_link';

function naverShoppingEnvKey(suffix: 'ID' | 'SECRET'): string {
    return ['NAVER', 'SHOPPING', 'CLIENT', suffix].join('_');
}

function naverShoppingHeaderName(suffix: 'Id' | 'Secret'): string {
    return ['X', 'Naver', 'Client', suffix].join('-');
}

export const naverShoppingPermissionRequirements: PlatformPermissionRequirements = {
    sourceName: naverShoppingPlatformName,
    sourceType: 'naver_shopping_search_api',
    permissionType: 'official_search_api_terms',
    requiredData: [
        'product_title',
        'product_url',
        'seller_name',
        'thumbnail_url',
        'listed_price',
        'price_snapshot'
    ],
    requireDisplay: true,
    requirePriceStorage: true,
    requireImageDisplay: true
};

function stringValue(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function numberValue(value: number | string | null | undefined): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const parsed = Number(value.replace(/,/g, '').trim());
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function clampInteger(value: number | string | null | undefined, min: number, max: number, fallback: number): number {
    const parsed = numberValue(value);
    if (parsed === null) return fallback;
    return Math.max(min, Math.min(max, Math.floor(parsed)));
}

function truthyEnvFlag(value: string | null): boolean {
    if (!value) return false;
    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

function envValue(env: Record<string, string | undefined>, key: string): string | null {
    return stringValue(env[key]);
}

function runtimeEnv(): Record<string, string | undefined> {
    const scope = globalThis as typeof globalThis & {
        process?: {
            env?: Record<string, string | undefined>;
        };
    };
    return scope.process?.env || {};
}

export function readNaverShoppingServerConfig(env: Record<string, string | undefined> = runtimeEnv()): NaverShoppingServerConfig {
    const timeoutMs = clampInteger(
        envValue(env, 'LOOKIT_NAVER_SHOPPING_TIMEOUT_MS'),
        100,
        30000,
        NAVER_SHOPPING_DEFAULT_TIMEOUT_MS
    );
    const retryCount = clampInteger(
        envValue(env, 'LOOKIT_NAVER_SHOPPING_RETRY_COUNT'),
        0,
        2,
        NAVER_SHOPPING_DEFAULT_RETRY_COUNT
    );
    const retryDelayMs = clampInteger(
        envValue(env, 'LOOKIT_NAVER_SHOPPING_RETRY_DELAY_MS'),
        0,
        5000,
        NAVER_SHOPPING_DEFAULT_RETRY_DELAY_MS
    );
    const cacheTtlMs = clampInteger(
        envValue(env, 'LOOKIT_NAVER_SHOPPING_CACHE_TTL_MS'),
        1000,
        5 * 60 * 1000,
        NAVER_SHOPPING_DEFAULT_CACHE_TTL_MS
    );
    const dailyLimit = numberValue(envValue(env, 'LOOKIT_NAVER_SHOPPING_DAILY_LIMIT'));

    return {
        enabled: truthyEnvFlag(envValue(env, 'LOOKIT_NAVER_SHOPPING_ENABLED')),
        clientId: envValue(env, naverShoppingEnvKey('ID')),
        clientSecret: envValue(env, naverShoppingEnvKey('SECRET')),
        baseUrl: envValue(env, 'LOOKIT_NAVER_SHOPPING_BASE_URL') || naverShoppingDefaultBaseUrl,
        timeoutMs,
        retryCount,
        retryDelayMs,
        cacheTtlMs,
        dailyLimit: Math.max(
            0,
            Math.min(
                NAVER_SHOPPING_OFFICIAL_DAILY_LIMIT,
                Math.floor(dailyLimit ?? NAVER_SHOPPING_OFFICIAL_DAILY_LIMIT)
            )
        )
    };
}

function normalizeConfig(options: NaverShoppingPlatformAdapterOptions): NaverShoppingServerConfig {
    const base = readNaverShoppingServerConfig(options.env);
    const overrides = options.config || {};
    const overrideNullableString = (value: string | null | undefined, fallback: string | null): string | null => (
        value === undefined ? fallback : value
    );

    return {
        enabled: overrides.enabled ?? base.enabled,
        clientId: overrideNullableString(overrides.clientId, base.clientId),
        clientSecret: overrideNullableString(overrides.clientSecret, base.clientSecret),
        baseUrl: overrides.baseUrl || base.baseUrl,
        timeoutMs: overrides.timeoutMs ?? base.timeoutMs,
        retryCount: Math.max(0, Math.min(2, Math.floor(overrides.retryCount ?? base.retryCount))),
        retryDelayMs: Math.max(0, Math.min(5000, Math.floor(overrides.retryDelayMs ?? base.retryDelayMs))),
        cacheTtlMs: Math.max(1000, Math.min(5 * 60 * 1000, Math.floor(overrides.cacheTtlMs ?? base.cacheTtlMs))),
        dailyLimit: Math.max(
            0,
            Math.min(
                NAVER_SHOPPING_OFFICIAL_DAILY_LIMIT,
                Math.floor(overrides.dailyLimit ?? base.dailyLimit)
            )
        )
    };
}

export function naverShoppingRuntimeState(config: NaverShoppingServerConfig): NaverShoppingAdapterRuntimeState {
    const credentialsConfigured = Boolean(config.clientId && config.clientSecret);
    if (!config.enabled) {
        return {
            enabled: false,
            credentialsConfigured,
            providerState: 'api_pending',
            sourceStatus: 'api_pending',
            reason: 'disabled'
        };
    }
    if (!credentialsConfigured) {
        return {
            enabled: true,
            credentialsConfigured: false,
            providerState: 'api_pending',
            sourceStatus: 'api_pending',
            reason: 'missing_credentials'
        };
    }
    return {
        enabled: true,
        credentialsConfigured: true,
        providerState: 'ready',
        sourceStatus: 'image_match_ready',
        reason: 'ready'
    };
}

function stableHash(value: string): string {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function dayKey(now: Date): string {
    return now.toISOString().slice(0, 10);
}

function normalizeSort(value: string | null | undefined): NaverShoppingSort {
    return allowedSorts.includes(value as NaverShoppingSort) ? value as NaverShoppingSort : 'sim';
}

function normalizeCategoryKey(value: string | null | undefined): string {
    const normalized = stringValue(value);
    return normalized || 'all';
}

function pageFromStart(start: number, display: number): number {
    return Math.max(1, Math.floor((start - 1) / Math.max(1, display)) + 1);
}

function startFromPage(page: number | string | null | undefined, display: number): number | null {
    if (page === null || page === undefined) return null;
    const parsed = numberValue(page);
    if (parsed === null) return null;
    return Math.min(1000, Math.max(1, ((Math.max(1, Math.floor(parsed)) - 1) * display) + 1));
}

export function naverShoppingCacheKeyParts(input: {
    query: string;
    category?: string | null;
    sort: NaverShoppingSort;
    start: number;
    display: number;
}): NaverShoppingCacheKeyParts {
    return {
        provider: naverShoppingProviderId,
        version: 'v1',
        queryHash: stableHash(input.query.trim().toLowerCase()),
        category: normalizeCategoryKey(input.category),
        sort: input.sort,
        page: pageFromStart(input.start, input.display),
        display: input.display
    };
}

export function buildNaverShoppingCacheKey(parts: NaverShoppingCacheKeyParts): string {
    return [
        parts.provider,
        parts.version,
        `category=${stableHash(parts.category)}`,
        `sort=${parts.sort}`,
        `page=${parts.page}`,
        `display=${parts.display}`,
        `query=${parts.queryHash}`
    ].join('|');
}

function cacheEvidence(input: {
    key: string;
    parts: NaverShoppingCacheKeyParts;
    ttlMs: number;
    cachedAt: string | null;
    expiresAt: string | null;
    hit: boolean;
}): NaverShoppingCacheEvidence {
    return {
        cache_key: input.key,
        cache_key_parts: cloneData(input.parts),
        cache_ttl_ms: input.ttlMs,
        cached_at: input.cachedAt,
        cache_expires_at: input.expiresAt,
        cache_hit: input.hit
    };
}

export class NaverShoppingInMemorySearchCache implements NaverShoppingSearchCache {
    private readonly entries = new Map<string, NaverShoppingCacheEntry>();

    get(key: string, now: Date): NaverShoppingCacheEntry | null {
        const entry = this.entries.get(key) || null;
        if (!entry) return null;

        if (new Date(entry.expiresAt).getTime() <= now.getTime()) {
            this.entries.delete(key);
            return null;
        }

        return cloneData(entry);
    }

    set(key: string, parts: NaverShoppingCacheKeyParts, rawListings: NaverShoppingRawListing[], now: Date, ttlMs: number): NaverShoppingCacheEntry {
        const cachedAt = now.toISOString();
        const expiresAt = new Date(now.getTime() + ttlMs).toISOString();
        const entry: NaverShoppingCacheEntry = {
            key,
            parts: cloneData(parts),
            rawListings: cloneData(rawListings),
            cachedAt,
            expiresAt,
            ttlMs
        };
        this.entries.set(key, cloneData(entry));
        return cloneData(entry);
    }
}

export class NaverShoppingInMemoryRateLimitGuard implements NaverShoppingRateLimitGuard {
    private readonly countsByDay = new Map<string, number>();

    tryConsume(now: Date, limit: number): NaverShoppingRateLimitDecision {
        const key = dayKey(now);
        const current = this.countsByDay.get(key) || 0;
        const normalizedLimit = Math.max(0, Math.floor(limit));

        if (current >= normalizedLimit) {
            return {
                allowed: false,
                dayKey: key,
                consumed: current,
                remaining: 0,
                limit: normalizedLimit
            };
        }

        const consumed = current + 1;
        this.countsByDay.set(key, consumed);
        return {
            allowed: true,
            dayKey: key,
            consumed,
            remaining: Math.max(0, normalizedLimit - consumed),
            limit: normalizedLimit
        };
    }

    getSnapshot(now: Date, limit: number): NaverShoppingRateLimitDecision {
        const key = dayKey(now);
        const normalizedLimit = Math.max(0, Math.floor(limit));
        const consumed = this.countsByDay.get(key) || 0;

        return {
            allowed: consumed < normalizedLimit,
            dayKey: key,
            consumed,
            remaining: Math.max(0, normalizedLimit - consumed),
            limit: normalizedLimit
        };
    }
}

export function createNaverShoppingInMemorySearchCache(): NaverShoppingInMemorySearchCache {
    return new NaverShoppingInMemorySearchCache();
}

export function createNaverShoppingInMemoryRateLimitGuard(): NaverShoppingInMemoryRateLimitGuard {
    return new NaverShoppingInMemoryRateLimitGuard();
}

function normalizeFilter(value: string | null | undefined): NaverShoppingFilter | undefined {
    return allowedFilters.includes(value as NaverShoppingFilter) ? value as NaverShoppingFilter : undefined;
}

function normalizeExclude(value: string | string[] | null | undefined): string | undefined {
    const parts = Array.isArray(value)
        ? value
        : typeof value === 'string'
            ? value.split(/[,:|]/)
            : [];
    const selected = parts
        .map((part) => part.trim())
        .filter((part): part is NaverShoppingExclude => allowedExcludes.includes(part as NaverShoppingExclude));
    const unique = Array.from(new Set(selected));

    return unique.length > 0 ? unique.join(':') : undefined;
}

export function buildNaverShoppingSearchRequest(input: NaverShoppingSearchRequestInput): NaverShoppingHttpRequest {
    const query = stringValue(input.query);
    if (!query) {
        throw new NaverShoppingProviderError({
            kind: 'invalid_request',
            sourceStatus: 'api_pending',
            status: 400,
            message: '네이버 쇼핑 검색어가 필요합니다.'
        });
    }

    const display = clampInteger(input.display, 1, 100, 10);
    const start = startFromPage(input.page, display) ?? clampInteger(input.start, 1, 1000, 1);
    const request: NaverShoppingHttpRequest = {
        query,
        display,
        start,
        sort: normalizeSort(input.sort)
    };
    const filter = normalizeFilter(input.filter);
    const exclude = normalizeExclude(input.exclude);

    if (filter) request.filter = filter;
    if (exclude) request.exclude = exclude;
    return request;
}

function buildUrl(baseUrl: string, request: NaverShoppingHttpRequest): string {
    const url = new URL(baseUrl);
    url.searchParams.set('query', request.query);
    url.searchParams.set('display', String(request.display));
    url.searchParams.set('start', String(request.start));
    url.searchParams.set('sort', request.sort);
    if (request.filter) url.searchParams.set('filter', request.filter);
    if (request.exclude) url.searchParams.set('exclude', request.exclude);
    return url.toString();
}

async function responseBody(response: Response): Promise<NaverShoppingApiResponse | NaverShoppingApiErrorBody | null> {
    try {
        return await response.json() as NaverShoppingApiResponse | NaverShoppingApiErrorBody;
    } catch {
        return null;
    }
}

export class NaverShoppingFetchHttpClient implements NaverShoppingHttpClient {
    async search(request: NaverShoppingHttpRequest, context: NaverShoppingHttpClientContext): Promise<NaverShoppingHttpResponse> {
        const abortController = new AbortController();
        const timeout = setTimeout(() => abortController.abort(), context.timeoutMs);

        try {
            const headers: Record<string, string> = {
                Accept: 'application/json'
            };
            headers[naverShoppingHeaderName('Id')] = context.clientId;
            headers[naverShoppingHeaderName('Secret')] = context.clientSecret;

            const response = await fetch(buildUrl(context.baseUrl, request), {
                method: 'GET',
                headers,
                signal: abortController.signal
            });

            return {
                status: response.status,
                body: await responseBody(response)
            };
        } finally {
            clearTimeout(timeout);
        }
    }
}

export interface NaverShoppingProviderErrorInput {
    kind: NaverShoppingProviderErrorKind;
    sourceStatus: SourceStatus;
    status: number | null;
    message: string;
}

export class NaverShoppingProviderError extends Error {
    readonly kind: NaverShoppingProviderErrorKind;
    readonly sourceStatus: SourceStatus;
    readonly source_status: SourceStatus;
    readonly status: number | null;

    constructor(input: NaverShoppingProviderErrorInput) {
        super(input.message);
        this.name = 'NaverShoppingProviderError';
        this.kind = input.kind;
        this.sourceStatus = input.sourceStatus;
        this.source_status = input.sourceStatus;
        this.status = input.status;
    }
}

function providerErrorKindForStatus(status: number): NaverShoppingProviderErrorKind {
    if (status === 400) return 'invalid_request';
    if (status === 403) return 'permission_denied';
    if (status === 429) return 'rate_limited';
    if (status >= 500) return 'provider_unavailable';
    return 'network_error';
}

function providerErrorMessage(kind: NaverShoppingProviderErrorKind): string {
    if (kind === 'invalid_request') return '네이버 쇼핑 요청 파라미터를 확인해야 합니다.';
    if (kind === 'permission_denied') return '네이버 쇼핑 API 권한 또는 사용 조건을 확인해야 합니다.';
    if (kind === 'rate_limited') return '네이버 쇼핑 API 호출 한도에 도달했습니다.';
    if (kind === 'provider_unavailable') return '네이버 쇼핑 API 응답이 일시적으로 불안정합니다.';
    if (kind === 'network_timeout') return '네이버 쇼핑 API 요청 시간이 초과됐습니다.';
    return '네이버 쇼핑 API 연결에 실패했습니다.';
}

function sourceStatusForProviderError(kind: NaverShoppingProviderErrorKind): SourceStatus {
    return kind === 'invalid_request' ? 'needs_verification' : 'api_pending';
}

function isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

export function toNaverShoppingProviderError(error: unknown, status: number | null = null): NaverShoppingProviderError {
    if (error instanceof NaverShoppingProviderError) return error;

    const kind = status !== null
        ? providerErrorKindForStatus(status)
        : isAbortError(error)
            ? 'network_timeout'
            : 'network_error';

    return new NaverShoppingProviderError({
        kind,
        sourceStatus: sourceStatusForProviderError(kind),
        status,
        message: providerErrorMessage(kind)
    });
}

function shouldRetryProviderError(error: NaverShoppingProviderError): boolean {
    return (
        error.status === null &&
        (error.kind === 'network_timeout' || error.kind === 'network_error')
    );
}

function rawListingsWithCacheEvidence(
    rawListings: NaverShoppingRawListing[],
    cache: NaverShoppingCacheEvidence
): NaverShoppingRawListing[] {
    return rawListings.map((rawListing) => {
        const next = cloneData(rawListing);
        next.freshness = {
            ...next.freshness,
            cachedAt: cache.cached_at,
            cached_at: cache.cached_at
        };
        next.raw_payload = {
            ...next.raw_payload,
            cache: cloneData(cache)
        };
        return next;
    });
}

function parsePrice(value: string | number | null | undefined): number {
    const parsed = numberValue(typeof value === 'number' ? value : value || null);
    return parsed === null ? 0 : Math.max(0, Math.round(parsed));
}

function decodeHtmlEntities(value: string): string {
    return value
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

function cleanNaverText(value: string | null | undefined): string {
    return decodeHtmlEntities(value || '')
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

function safeHttpUrl(value: string | null | undefined): string | null {
    const trimmed = stringValue(value);
    if (!trimmed) return null;

    try {
        const url = new URL(trimmed);
        return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
    } catch {
        return null;
    }
}

function safeUrl(value: string | null | undefined, fallback: string): string {
    return safeHttpUrl(value) || fallback;
}

function sellerId(mallName: string | null): string {
    return `seller-naver-shopping-${stableHash(mallName || 'unknown')}`;
}

function productTypeStatus(value: string | null | undefined): NaverShoppingProductTypeStatus {
    const code = cleanNaverText(value) || null;
    const normalized = (code || '').toLowerCase();
    let kind: NaverShoppingProductTypeKind = 'unknown';
    let label = '상품 유형 확인 필요';

    if (normalized === '2' || /비\s*매칭|unmatched/.test(normalized)) {
        kind = 'price_compare_unmatched';
        label = '가격비교 비매칭 상품';
    } else if (normalized === '1' || /가격\s*비교/.test(normalized)) {
        kind = 'price_compare';
        label = '가격비교 상품';
    } else if (normalized === '3' || /중고|used/.test(normalized)) {
        kind = 'used';
        label = '중고 상품';
    } else if (normalized === '4' || /판매\s*예정|pre/.test(normalized)) {
        kind = 'pre_release';
        label = '판매예정 상품';
    } else if (normalized === '5' || /단종|discontinued/.test(normalized)) {
        kind = 'discontinued';
        label = '단종 상품';
    } else if (normalized === '6' || /판매\s*중지|stopped|suspended/.test(normalized)) {
        kind = 'sale_stopped';
        label = '판매중지 상품';
    }

    return {
        code,
        kind,
        label,
        excludedFromLowest: ['used', 'pre_release', 'discontinued', 'sale_stopped'].includes(kind)
    };
}

function priceNoteForStatus(status: NaverShoppingProductTypeStatus, listedPrice: number): string {
    const notes = ['lprice는 배송비/공개 쿠폰/옵션 재고가 빠진 가격 앵커이며 LOOKIT 실구매가 확정값이 아닙니다.'];
    if (listedPrice <= 0) notes.push('lprice가 0이므로 가격 확인이 필요합니다.');
    if (status.excludedFromLowest) notes.push(`${status.label}은 기본 최저가 후보에서 제외합니다.`);
    return notes.join(' ');
}

function categoryFromNaverItem(item: NaverShoppingApiItem): {
    rootId: ProductCategoryRootId | string;
    rootLabel: ProductCategoryRoot;
    subcategoryId: string | null;
    subcategoryLabel: ProductSubcategory | null;
    path: ProductCategoryPath;
    text: string;
} {
    const categories = [item.category1, item.category2, item.category3, item.category4]
        .map((category) => cleanNaverText(category))
        .filter(Boolean);
    const text = categories.join(' > ');
    const searchable = text.toLowerCase();
    let rootId: ProductCategoryRootId | string = 'unknown';
    let rootLabel: ProductCategoryRoot = '확인 필요';
    let subcategoryId: string | null = null;
    let subcategoryLabel: ProductSubcategory | null = null;

    if (/원피스|dress/.test(searchable)) {
        rootId = 'dress';
        rootLabel = '원피스';
    } else if (/코트|자켓|재킷|점퍼|패딩|outer|jacket|coat/.test(searchable)) {
        rootId = 'outer';
        rootLabel = '아우터';
    } else if (/바지|팬츠|데님|청바지|스커트|쇼츠|반바지|슬랙스|pants|jeans|skirt|shorts|slacks/.test(searchable)) {
        rootId = 'bottom';
        rootLabel = '하의';
        if (/스커트|skirt/.test(searchable)) subcategoryLabel = '스커트';
        else if (/데님|청바지|jeans/.test(searchable)) subcategoryLabel = '데님';
        else if (/쇼츠|반바지|shorts/.test(searchable)) subcategoryLabel = '쇼츠';
        else subcategoryLabel = '팬츠';
    } else if (/신발|슈즈|운동화|shoes|sneakers/.test(searchable)) {
        rootId = 'shoes';
        rootLabel = '신발';
    } else if (/가방|백|bag/.test(searchable)) {
        rootId = 'bag';
        rootLabel = '가방';
    } else if (/잡화|액세서리|accessory/.test(searchable)) {
        rootId = 'accessory';
        rootLabel = '액세서리';
    } else if (/상의|블라우스|티셔츠|셔츠|니트|가디건|top|shirt|blouse|knit|cardigan/.test(searchable)) {
        rootId = 'top';
        rootLabel = '상의';
        if (/블라우스|blouse/.test(searchable)) subcategoryLabel = '블라우스';
        else if (/티셔츠|t-shirt|tee/.test(searchable)) subcategoryLabel = '티셔츠';
        else if (/니트|knit/.test(searchable)) subcategoryLabel = '니트';
        else if (/가디건|cardigan/.test(searchable)) subcategoryLabel = '가디건';
        else if (/셔츠|shirt/.test(searchable)) subcategoryLabel = '셔츠';
    }

    if (subcategoryLabel) subcategoryId = `${rootId}:${String(subcategoryLabel).toLowerCase()}`;

    return {
        rootId,
        rootLabel,
        subcategoryId,
        subcategoryLabel,
        text,
        path: {
            root_id: rootId,
            root_label: rootLabel,
            subcategory_id: subcategoryId,
            subcategory_label: subcategoryLabel,
            path_label: subcategoryLabel ? `${rootLabel} > ${subcategoryLabel}` : rootLabel
        }
    };
}

function naverFreshness(checkedAt: string, collectedAt: string, cachedAt: string | null = null): ListingFreshness {
    return normalizeListingFreshness({
        status: 'api_pending',
        checkedAt,
        collectedAt,
        cachedAt,
        refreshInterval: 180,
        failureReason: '배송비, 공개 쿠폰, 옵션 재고는 네이버 쇼핑 검색 응답만으로 확인되지 않습니다.',
        confidence: 'low',
        confidenceScore: 0.48,
        sourceStatus: 'needs_verification'
    }, new Date(checkedAt));
}

function rawListingFromNaverItem(
    item: NaverShoppingApiItem,
    request: NaverShoppingHttpRequest,
    productClusterId: string,
    checkedAt: string,
    collectedAt: string,
    cache: NaverShoppingCacheEvidence
): NaverShoppingRawListing {
    const title = cleanNaverText(item.title);
    const mallName = cleanNaverText(item.mallName) || null;
    const brandName = cleanNaverText(item.brand) || null;
    const makerName = cleanNaverText(item.maker) || null;
    const productId = cleanNaverText(item.productId);
    const platformListingId = productId || `naver-shopping-${stableHash(`${title}|${item.link || ''}`)}`;
    const listedPrice = parsePrice(item.lprice);
    const category = categoryFromNaverItem(item);
    const typeStatus = productTypeStatus(item.productType);
    const sourceStatus: SourceStatus = 'needs_verification';
    const freshness = naverFreshness(checkedAt, collectedAt, cache.cached_at);
    const unknownPriceComponents: PriceUnknownComponent[] = [
        'public_coupon',
        'shipping_fee',
        'free_shipping_condition',
        'availability',
        'option_stock',
        'size_stock'
    ];
    if (listedPrice <= 0) unknownPriceComponents.unshift('listed_price', 'sale_price');
    const priceVerificationStatus: PriceVerificationStatus = 'needs_verification';
    const priceNote = priceNoteForStatus(typeStatus, listedPrice);

    return {
        id: `listing-naver-shopping-${platformListingId}`,
        platform_id: naverShoppingPlatformId,
        platform_kind: 'naver_shopping' as PlatformAdapterKind,
        platform_listing_id: platformListingId,
        seller_id: sellerId(mallName),
        seller_product_id: null,
        external_product_id: productId || null,
        product_cluster_id: productClusterId,
        title,
        product_url: safeUrl(item.link, 'https://shopping.naver.com'),
        image_urls: safeHttpUrl(item.image) ? [safeHttpUrl(item.image) as string] : [],
        checked_at: checkedAt,
        collected_at: collectedAt,
        freshness,
        source_status: sourceStatus,
        confidence: 0.48,
        price: {
            listed_price: listedPrice,
            sale_price: listedPrice,
            instant_discount: 0,
            public_coupon_discount: 0,
            base_shipping_fee: 0,
            shipping_fee: 0,
            free_shipping_condition: null,
            free_shipping_price_basis: null,
            public_effective_price: calculatePublicEffectivePrice({
                listed_price: listedPrice,
                instant_discount: 0,
                public_coupon_discount: 0,
                shipping_fee: 0,
                price_verification_status: priceVerificationStatus,
                unknown_price_components: unknownPriceComponents,
                availability_status: 'checking'
            }),
            price_verification_status: priceVerificationStatus,
            unknown_price_components: unknownPriceComponents,
            currency: 'KRW'
        },
        stock: {
            availability_status: 'checking' as AvailabilityStatus,
            options: []
        },
        raw_payload: {
            provider_id: naverShoppingProviderId,
            api: 'naver_shopping_search',
            query_hash: stableHash(request.query),
            cache,
            request: {
                display: request.display,
                start: request.start,
                sort: request.sort,
                filter: request.filter,
                exclude: request.exclude
            },
            item,
            price_note: priceNote
        },
        naver_product_type: cleanNaverText(item.productType) || null,
        naver_product_type_status: typeStatus,
        mall_name: mallName,
        seller_display_name: mallName,
        platform_display_name: naverShoppingPlatformName,
        brand_name: brandName,
        maker_name: makerName,
        category_path_text: category.text,
        category_confidence: category.text ? 0.46 : 0.18,
        category_needs_verification: true,
        category_source: 'naver_category_hint',
        search_hints: {
            brandName,
            makerName,
            categoryPathText: category.text,
            sameProductEvidence: false,
            confidence: 'low',
            needsVerification: true
        },
        price_candidate_status: typeStatus.excludedFromLowest || listedPrice <= 0 ? 'excluded_from_lowest' : 'needs_verification',
        unknown_price_components: unknownPriceComponents
    };
}

function collectedAtFromResponse(response: NaverShoppingApiResponse, fallback: string): string {
    const candidate = response.lastBuildDate ? new Date(response.lastBuildDate) : null;
    return candidate && Number.isFinite(candidate.getTime()) ? candidate.toISOString() : fallback;
}

export class NaverShoppingPlatformAdapter implements PlatformAdapter<NaverShoppingRawListing> {
    readonly platform_id = naverShoppingPlatformId;
    readonly platform_name = naverShoppingPlatformName;
    readonly platform_kind = 'naver_shopping' as PlatformAdapterKind;

    private readonly config: NaverShoppingServerConfig;
    private readonly client: NaverShoppingHttpClient;
    private readonly cache: NaverShoppingSearchCache | null;
    private readonly rateLimitGuard: NaverShoppingRateLimitGuard | null;
    private readonly now: () => Date;
    private readonly sleep: (ms: number) => Promise<void>;
    private readonly rawListingsById = new Map<string, NaverShoppingRawListing>();
    private retryStats: NaverShoppingRetryStats = {
        attempts: 0,
        retries: 0,
        lastErrorKind: null
    };

    constructor(options: NaverShoppingPlatformAdapterOptions = {}) {
        this.config = normalizeConfig(options);
        this.client = options.client || new NaverShoppingFetchHttpClient();
        this.cache = options.cache === null ? null : options.cache || createNaverShoppingInMemorySearchCache();
        this.rateLimitGuard = options.rateLimitGuard === null ? null : options.rateLimitGuard || createNaverShoppingInMemoryRateLimitGuard();
        this.now = options.now || (() => new Date());
        this.sleep = options.sleep || ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)));
    }

    getRuntimeState(): NaverShoppingAdapterRuntimeState {
        return naverShoppingRuntimeState(this.config);
    }

    getRateLimitState(): NaverShoppingRateLimitDecision | null {
        return this.rateLimitGuard ? this.rateLimitGuard.getSnapshot(this.now(), this.config.dailyLimit) : null;
    }

    getRetryStats(): NaverShoppingRetryStats {
        return { ...this.retryStats };
    }

    private rememberRawListings(rawListings: NaverShoppingRawListing[]) {
        rawListings.forEach((raw) => {
            this.rawListingsById.set(raw.id, raw);
            this.rawListingsById.set(raw.platform_listing_id, raw);
            if (raw.external_product_id) this.rawListingsById.set(raw.external_product_id, raw);
        });
    }

    private async requestWithRetry(
        httpRequest: NaverShoppingHttpRequest,
        clientId: string,
        clientSecret: string
    ): Promise<NaverShoppingHttpResponse> {
        let attempt = 0;
        this.retryStats = {
            attempts: 0,
            retries: 0,
            lastErrorKind: null
        };

        while (attempt <= this.config.retryCount) {
            attempt += 1;
            this.retryStats.attempts = attempt;

            try {
                const response = await this.client.search(httpRequest, {
                    baseUrl: this.config.baseUrl,
                    clientId,
                    clientSecret,
                    timeoutMs: this.config.timeoutMs
                });

                if (response.status >= 400) {
                    throw toNaverShoppingProviderError(response.body, response.status);
                }

                return response;
            } catch (error) {
                const providerError = toNaverShoppingProviderError(error);
                this.retryStats.lastErrorKind = providerError.kind;
                if (!shouldRetryProviderError(providerError) || attempt > this.config.retryCount) {
                    throw providerError;
                }

                this.retryStats.retries += 1;
                if (this.config.retryDelayMs > 0) await this.sleep(this.config.retryDelayMs);
            }
        }

        throw toNaverShoppingProviderError(new Error('network retry failed'));
    }

    async searchProducts(request: PlatformSearchProductsRequest = {}): Promise<NaverShoppingRawListing[]> {
        const state = this.getRuntimeState();
        if (state.providerState !== 'ready') return [];

        const httpRequest = buildNaverShoppingSearchRequest({
            query: request.query,
            category: request.selectedCategoryId,
            display: request.display ?? request.limit,
            page: request.page,
            start: 1,
            sort: request.sort || 'sim',
            exclude: ['used', 'rental', 'cbshop']
        });
        const cacheParts = naverShoppingCacheKeyParts({
            query: httpRequest.query,
            category: request.selectedCategoryId,
            sort: httpRequest.sort,
            start: httpRequest.start,
            display: httpRequest.display
        });
        const cacheKey = buildNaverShoppingCacheKey(cacheParts);
        const now = this.now();
        const cachedEntry = this.cache?.get(cacheKey, now) || null;
        if (cachedEntry) {
            const cachedRawListings = rawListingsWithCacheEvidence(cachedEntry.rawListings, cacheEvidence({
                key: cachedEntry.key,
                parts: cachedEntry.parts,
                ttlMs: cachedEntry.ttlMs,
                cachedAt: cachedEntry.cachedAt,
                expiresAt: cachedEntry.expiresAt,
                hit: true
            }));
            this.rememberRawListings(cachedRawListings);
            return cachedRawListings;
        }

        const clientId = this.config.clientId;
        const clientSecret = this.config.clientSecret;
        if (!clientId || !clientSecret) return [];

        try {
            const budget = this.rateLimitGuard?.tryConsume(now, this.config.dailyLimit) || null;
            if (budget && !budget.allowed) return [];

            const response = await this.requestWithRetry(httpRequest, clientId, clientSecret);

            const body = response.body as NaverShoppingApiResponse | null;
            const checkedAt = now.toISOString();
            const collectedAt = body ? collectedAtFromResponse(body, checkedAt) : checkedAt;
            const productClusterId = request.productClusterIds?.[0] || 'cluster-naver-shopping-unmapped';
            const missCache = cacheEvidence({
                key: cacheKey,
                parts: cacheParts,
                ttlMs: this.config.cacheTtlMs,
                cachedAt: null,
                expiresAt: null,
                hit: false
            });
            const rawListings = (body?.items || []).map((item) => rawListingFromNaverItem(
                item,
                httpRequest,
                productClusterId,
                checkedAt,
                collectedAt,
                missCache
            ));

            this.rememberRawListings(rawListings);
            this.cache?.set(cacheKey, cacheParts, rawListings, now, this.config.cacheTtlMs);

            return rawListings;
        } catch (error) {
            throw toNaverShoppingProviderError(error);
        }
    }

    async getListingDetail(platformListingId: string): Promise<PlatformListingDetail<NaverShoppingRawListing> | null> {
        const listing = this.rawListingsById.get(platformListingId) || null;
        if (!listing) return null;

        return {
            platform_id: this.platform_id,
            checked_at: listing.checked_at,
            collected_at: listing.collected_at,
            raw_listing: JSON.parse(JSON.stringify(listing)) as NaverShoppingRawListing
        };
    }

    async getPriceAndStock(platformListingId: string): Promise<PlatformPriceAndStock | null> {
        const detail = await this.getListingDetail(platformListingId);
        if (!detail) return null;
        const listing = this.normalizeListing(detail.raw_listing);

        return {
            listing_id: listing.id,
            product_cluster_id: listing.product_cluster_id,
            platform_id: listing.platform_id,
            checked_at: listing.checked_at || listing.last_checked_at,
            collected_at: listing.collected_at || listing.last_checked_at,
            listed_price: listing.listed_price,
            sale_price: listing.sale_price,
            instant_discount: listing.instant_discount,
            public_coupon_discount: listing.public_coupon_discount,
            base_shipping_fee: listing.base_shipping_fee,
            shipping_fee: listing.shipping_fee,
            free_shipping_condition: listing.free_shipping_condition,
            free_shipping_price_basis: listing.free_shipping_price_basis || null,
            public_effective_price: listing.public_effective_price,
            price_verification_status: listing.price_verification_status,
            unknown_price_components: listing.unknown_price_components?.slice(),
            currency: listing.currency,
            availability_status: listing.availability_status,
            options: JSON.parse(JSON.stringify(listing.options)) as Listing['options'],
            freshness: JSON.parse(JSON.stringify(listing.freshness)) as ListingFreshness,
            source_status: listing.source_status,
            confidence: listing.confidence || 0
        };
    }

    normalizeListing(rawListing: NaverShoppingRawListing): Listing {
        const category = categoryFromNaverItem(rawListing.raw_payload.item);
        const currency: CurrencyCode = rawListing.price.currency;

        return {
            id: rawListing.id,
            product_cluster_id: rawListing.product_cluster_id,
            platform_id: rawListing.platform_id,
            seller_id: rawListing.seller_id,
            external_product_id: rawListing.external_product_id,
            platform_listing_id: rawListing.platform_listing_id,
            seller_product_id: rawListing.seller_product_id,
            title: rawListing.title,
            description: [
                rawListing.mall_name ? `${rawListing.mall_name} 네이버 쇼핑 검색 후보` : '네이버 쇼핑 검색 후보',
                '배송비, 공개 쿠폰, 옵션 재고는 별도 확인 필요'
            ].join('. '),
            product_url: rawListing.product_url,
            product_url_source: naverShoppingProductUrlSource,
            image_urls: rawListing.image_urls.slice(),
            category_root_id: category.rootId,
            category_root: category.rootLabel,
            subcategory_id: category.subcategoryId,
            subcategory: category.subcategoryLabel,
            category_path: category.path,
            listed_price: rawListing.price.listed_price,
            sale_price: rawListing.price.sale_price,
            instant_discount: rawListing.price.instant_discount,
            public_coupon_discount: rawListing.price.public_coupon_discount,
            base_shipping_fee: rawListing.price.base_shipping_fee,
            shipping_fee: rawListing.price.shipping_fee,
            free_shipping_condition: rawListing.price.free_shipping_condition,
            free_shipping_price_basis: rawListing.price.free_shipping_price_basis || null,
            public_effective_price: rawListing.price.public_effective_price || calculatePublicEffectivePrice(rawListing.price),
            price_verification_status: rawListing.price.price_verification_status,
            unknown_price_components: rawListing.price.unknown_price_components?.slice(),
            currency,
            availability_status: rawListing.stock.availability_status,
            variant_ids: [],
            options: [],
            benefits: [],
            last_checked_at: rawListing.checked_at,
            checked_at: rawListing.checked_at,
            collected_at: rawListing.collected_at,
            confidence: rawListing.confidence,
            freshness: JSON.parse(JSON.stringify(rawListing.freshness)) as ListingFreshness,
            source_status: rawListing.source_status,
            price_note: rawListing.raw_payload.price_note,
            seller_display_name: rawListing.seller_display_name,
            platform_display_name: rawListing.platform_display_name,
            category_confidence: rawListing.category_confidence,
            category_needs_verification: rawListing.category_needs_verification,
            category_source: rawListing.category_source
        };
    }
}

export function createNaverShoppingPlatformAdapterRegistration(
    options: NaverShoppingPlatformAdapterRegistrationOptions = {}
): PlatformAdapterRegistration<NaverShoppingRawListing> {
    const adapter = new NaverShoppingPlatformAdapter(options);
    const state = adapter.getRuntimeState();

    return {
        adapter,
        providerMode: 'real',
        permissionEvidence: state.providerState === 'ready' ? options.permissionEvidence || null : null,
        permissionRequirements: {
            ...naverShoppingPermissionRequirements,
            now: options.permissionNow
        }
    };
}

export function createNaverShoppingPlatformAdapterRegistry(
    options: NaverShoppingPlatformAdapterRegistrationOptions = {}
): PermissionGuardedPlatformAdapterRegistry<NaverShoppingRawListing> {
    return new PermissionGuardedPlatformAdapterRegistry([
        createNaverShoppingPlatformAdapterRegistration(options)
    ]);
}
